import { useAppContext } from "@context/app";
import { useUI } from "@context/ui-managed-context";
import Button from "@elements/button";
import Modal from "@modules/layout/modal";
import BagIcon from "@svg/BagIcon";
import { CartItemModel, CartStep, IOrderPersonalization, ISkuModel } from "@types";
import { convertNumber } from "@utils/digits";
import { getNotifyConfig, updateCart } from "@utils/helpers";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { BsDash, BsPlus, BsTrash } from "react-icons/bs";
import CartItem from "./cartItem";
import ImagePrepare from "@elements/imagePrepare";
import useEvents from "@hooks/useEvents";
import { toast } from "react-toastify";
import FreeDelivery from "@elements/freeDelivery";
import RemoveItemToWishlistModal from "./RemoveItemToWishlistModal";
import { wishlistApi } from "gate";
import Personalizations from "./personalizations";
import { CartProblemType } from "@templates/cartContainer";


interface CartProps {
  changeStep: (value: CartStep | "checkout") => void;
  action: (value: {
    next?: CartStep;
    current: CartStep;
    returnRelatedItems?: (data: ISkuModel[], nextStep: CartStep | "checkout" | null) => void
    goCheckout?: boolean
  }) => void;
  cartProblems: {
    id: string | number;
    type: CartProblemType;
    count?: number;
  }[];
  removeProblems: () => void;
  extraItems: ISkuModel[];
  onReady: () => void;
  hasRelated: () => boolean
  personalizations: IOrderPersonalization[];
  personalizationSource: string;
}

const Cart: React.FC<CartProps> = ({
  changeStep,
  action,
  cartProblems,
  removeProblems,
  onReady,
  personalizations: initialPersonalizations,
  personalizationSource,
}) => {
  const { user, setUserCart, cart, currency, decimal, syncData, wishlistProductIds, setWishlistProductIds } = useAppContext();
  const { setModalView, openModal } = useUI();
  const { skuAdded, skuRemoved } = useEvents()
  const t = useTranslations();
  const router = useRouter();
  const [cartItems, setCartItems] = useState<CartItemModel[]>([]);
  const [openExtraModal, setOpenExtraModal] = useState(false)
  const [extraItems, setExtraItems] = useState<ISkuModel[]>([])
  const [extraList, setExtraList] = useState<{ data: ISkuModel, qty: number }[]>([])
  const [nextStep, setNextStep] = useState<CartStep | "checkout">("address")
  const [showWishlistModal, setShowWishlistModal] = useState(false);
  const [isAddingToWishlist, setIsAddingToWishlist] = useState(false);
  const [removedItem, setRemovedItem] = useState<CartItemModel | null>(null);
  const [personalizations, setPersonalizations] = useState<IOrderPersonalization[]>([]);
  const ItemsQty = cartItems.reduce((previousValue, currentValue) => {
    return previousValue + currentValue.qty;
  }, 0);
  useEffect(() => {
    setUserCart(updateCart());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCartItems(cart);
  }, [cart]);

  useEffect(() => {
    const list = []
    for (const iterator of extraItems || []) {
      list.push({ data: iterator, qty: 0 })
    }
    setExtraList([...list])
  }, [extraItems]);


  const extraSource = {
    from_page: "cart",
    from_entity: "",
    from_entity_slug: "",
    from_page_section: "Suggestion Modal",
    from_page_section_entity: "",
    from_page_section_slug: "",
  }


  const changeQty = (id: string | number, count: number) => {
    const data: CartItemModel[] = updateCart();
    let itemToRemove: CartItemModel | null = null;

    for (const item of data) {
      if (item.id === id) {
        if (item.digital_gift_ids?.length || 0 > 0) return;
        if (item.registry_contribution_ids?.length || 0 > 0) return;

        // If quantity is being reduced to 0, store item details for wishlist modal
        if (count === 0) {
          itemToRemove = { ...item };
        }

        item.qty = count;
        item.gifts = [...item.gifts.slice(0, count)];
      }
    }
    // Remove items with 0 quantity
    const filteredData = data.filter(item => item.qty > 0);
    setUserCart([...filteredData]);

    // Show wishlist modal if an item was completely removed
    if (itemToRemove && syncData?.allow_cart_wishlist_popup) {
      setRemovedItem(itemToRemove);
      setShowWishlistModal(true);
    }
  };

  const deleteItem = (id: string | number) => {
    // Find the item being deleted to store its details
    let itemToDelete: CartItemModel | undefined;

    // Remove item from cart
    const data = [...cartItems];
    setUserCart(data.filter((item) => {
      if (item.id === id) {
        itemToDelete = item;
      }
      return item.id !== id;
    }));

    // Show wishlist modal if item was found
    if (itemToDelete && syncData?.allow_cart_wishlist_popup && !wishlistProductIds.includes(itemToDelete.productId)) {
      setRemovedItem(itemToDelete);
      setShowWishlistModal(true);
    }
  };

  useEffect(() => {
    return () => {
      removeProblems();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLoginModal = () => {
    setModalView("LOGIN_VIEW", true);
    openModal();
  };



  const handleAddExtra = (sku: ISkuModel) => {
    const list = [...extraList]
    for (const iterator of list) {
      if (iterator.data.id === sku.id) {
        if (iterator.qty) {
          if (iterator.qty < iterator.data.buy_limit) {
            changeQty(iterator.data.id, (iterator.qty + 1))
          }
          else {
            toast.error(
              t("client_errors_onlyremaining").replace("@", `${iterator.data.buy_limit}`),
              getNotifyConfig()
            );
            return
          }
        }
        else {
          const _currentCart = updateCart()
          setUserCart(
            [
              ..._currentCart,
              {
                differentOptions: false,
                image: iterator.data?.medias[0].url || "",
                qty: 1,
                productName: iterator.data.product?.name,
                productSlug: iterator.data.product?.slug,
                skuName: iterator.data?.name,
                price: iterator.data?.price.price,
                raw_price: iterator.data?.price.raw_price,
                gifts: [],
                id: iterator.data?.id,
                buy_limit: iterator.data?.buy_limit,
                productId: iterator.data.product?.id,
                discount: iterator.data?.price.old_price
                  ? iterator.data.price.old_price - iterator.data.price.price
                  : 0,
                is_customizable: iterator.data.product?.is_customizable,
                skuSlug: iterator.data?.slug,
                oldPrice: iterator.data?.price.old_price,
                brand_id: iterator.data.product?.brand.id,
                brand_slug: iterator.data.product?.brand.slug,
                category_id: iterator.data.product?.category.id,
                category_slug: iterator.data.product?.category.slug,
                subcategory_id: iterator.data.product?.subcategory?.id,
                subcategory_slug: iterator.data.product?.subcategory?.slug,
              },
            ]
          );
        }
        iterator.qty += 1
        skuAdded(extraSource, iterator.data)
      }
    }
    setExtraList([...list])
  }
  const handleMinusExtra = (sku: ISkuModel) => {
    const list = [...extraList]
    for (const iterator of list) {
      if (iterator.data.id === sku.id) {
        if (iterator.qty > 1) {
          changeQty(iterator.data.id, (iterator.qty - 1))
        }
        else {
          deleteItem(iterator.data.id)
        }
        skuRemoved(extraSource, iterator.data, iterator.qty > 1 ? "Remove" : "Delete")
        iterator.qty -= 1
      }
    }
    setExtraList([...list])
  }

  const readyForNext = () => {
    onReady()
  }


  const handleLogin = () => {
    setOpenExtraModal(false);
    setTimeout(() => {
      openLoginModal()
    }, 300);

  }

  const handleAddToWishlist = async () => {
    if (!removedItem) return;

    // First, add to wishlist as normal
    try {
      setIsAddingToWishlist(true);
      const response = await wishlistApi.addToDefaultWishlist([removedItem.productId]).fetch();
      if (response.meta.code === 200) {
        // Update wishlist product IDs
        setWishlistProductIds([...new Set([...wishlistProductIds, removedItem.productId])]);
        toast.success(t("Item added to wishlist"));

        // After successfully adding to wishlist, check conditions to add to wishlist personalization tab
        // 1. User added the removed cart item to wishlist (we're here, so yes)

        // 2. Check if the removed item is in stock
        if (removedItem.buy_limit && removedItem.buy_limit > 0) {

          // 3. Check if there is a personalization tab of type "Wishlist"
          const wishlistPersonalization = personalizations?.find(p => p.type === "Wishlist");

          if (wishlistPersonalization) {

            // 4. Check if there is an item in the wishlist personalization products that matches the condition
            const existingItem = wishlistPersonalization.products.find(
              p => removedItem.id === p.default_sku_id
            );

            // 5. If no item was found, add the removed item to the wishlist personalization
            if (!existingItem) {
              // Convert CartItemModel to IProductDetailModel format to add to personalization
              const productToAdd: any = {
                id: removedItem.productId,
                name: removedItem.productName,
                slug: removedItem.productSlug,
                default_sku_id: removedItem.id,
                skus: [{
                  id: removedItem.id,
                  name: removedItem.skuName,
                  slug: removedItem.skuSlug,
                  buy_limit: removedItem.buy_limit,
                  price: {
                    price: removedItem.price,
                    raw_price: removedItem.raw_price,
                    old_price: removedItem.oldPrice,
                  },
                  medias: [{
                    url: removedItem.image,
                  }],
                }],
                brand: {
                  id: removedItem.brand_id,
                  slug: removedItem.brand_slug,
                },
                category: {
                  id: removedItem.category_id,
                  slug: removedItem.category_slug,
                },
                subcategory: removedItem.subcategory_id ? {
                  id: removedItem.subcategory_id,
                  slug: removedItem.subcategory_slug,
                } : undefined,
              };
              setPersonalizations((prev) => {
                const wishlistIndex = prev.findIndex(p => p.type === "Wishlist");
                if (wishlistIndex !== -1) {
                  const newPersonalizations = [...prev];
                  newPersonalizations[wishlistIndex] = {
                    ...wishlistPersonalization,
                    products: [...wishlistPersonalization.products, productToAdd],
                  };
                  return newPersonalizations;
                }
                return prev;
              });
            }
          }
        }
      }
    } catch (error) {
      console.error("Error adding item to wishlist:", error);
    } finally {
      setIsAddingToWishlist(false);
      setShowWishlistModal(false);
      setRemovedItem(null);
    }
  };

  const handleCancelWishlist = () => {
    setShowWishlistModal(false);
    setRemovedItem(null);
  };

  useEffect(() => {
    setPersonalizations(initialPersonalizations);
  }, [initialPersonalizations]);


  let continueAction: any;
  if (!user.isLoggedIn) {
    continueAction = openLoginModal;
  } else {
    continueAction = (nextStep: CartStep | "checkout" | null) => {
      changeStep(nextStep || "address")
    };
  }

  return (
    <div className="w-full mt-10 ">
      <Modal
        open={openExtraModal}
        onClose={() => { setOpenExtraModal(false); readyForNext() }}
        cancelable
        withoutBackground
      >
        <div className="md:w-[calc(100vw-8px)] sm:w-[calc(100vw-8px)] xs:w-[calc(100vw-8px)] w-[calc(100vw-8px)] max-w-[480px] min-h-[480px] h-[max-content] bg-white rounded-lg p-3 mx-auto">
          <div className="w-full mt-2 mb-5">
            <p className=" text-center font-normal text-xl text-[#333333]">{t("client_cart_wantanythingelse")}</p>
          </div>
          <div className="w-full min-h-[320px]">
            {extraList.map((item) => (
              <div className="w-full border rounded-xl p-3 border-[#EEEEEE] flex mb-3" key={item.data.id}>
                <div className="relative w-20 h-20 rounded-xl overflow-hidden">
                  <ImagePrepare media={item.data.medias[0]} alt={item.data.name} width={80} />
                </div>
                <div className="flex-1 px-2">
                  <p className=" text-start font-normal text-sm line-clamp-2 leading-6 px-1 mb-2">
                    {item.data.name}
                  </p>
                  <p className="w-full font-extrabold text-lg text-[#333] !text-start !justify-start left-to-right">
                    <span
                      style={{ direction: "rtl" }}
                      className="font-normal text-sm px-1 text-left"
                    >
                      {currency}
                    </span>
                    {item.data.price.old_price && <s className=" font-normal text-base px-1 ">
                      {convertNumber(router.locale, item.data.price.old_price.toFixed(decimal))
                      }
                    </s>}
                    {convertNumber(router.locale, (item.data.price.price).toFixed(decimal))}
                  </p>
                </div>
                <div className="">
                  {item.qty === 0 ? <Button
                    onClick={() => handleAddExtra(item.data)}
                    className=" h-8 shadow-md font-extrabold text-sm !w-20 rounded-full"
                    size="slim"
                    type="submit"
                    theme="blue"
                  >
                    {t("client_buttons_add")}
                  </Button> : <div
                    className="md:w-1/5 sm:w-full w-full"
                    style={{ direction: "ltr" }}
                  >
                    <div className=" flex flex-wrap  w-[max-content] m-auto items-center justify-center">
                      <button
                        className={`w-6 h-6 rounded-full border-[#008AF6] ${item.qty > 1 ? "border-2" : ""
                          }  text-[#008AF6] text-xl hover:text-primary hover:border-primary flex items-center justify-center`}
                      >
                        {item.qty > 1 ? (
                          <BsDash
                            size={24}
                            onClick={() =>
                              handleMinusExtra(item.data)
                            }
                          />
                        ) : (
                          <BsTrash
                            className=" cursor-pointer"
                            size={24}
                            onClick={() =>
                              handleMinusExtra(item.data)
                            }
                          />
                        )}
                      </button>
                      <p className="h-12 w-14 text-[#333] text-center flex justify-center items-center font-extrabold text-xl mx-2">
                        {convertNumber(router.locale, item.qty)}
                      </p>
                      <button
                        className={`w-6 h-6 rounded-full border-[#008AF6] border-2  text-[#008AF6] text-xl hover:text-primary hover:border-primary flex items-center justify-center disabled:text-gray-400 disabled:border-gray-400 disabled:cursor-not-allowed`}
                        onClick={() => handleAddExtra(item.data)}
                      >
                        <BsPlus size={24} />
                      </button>
                    </div>
                  </div>}
                </div>
              </div>
            ))}


          </div>
          <div className="w-full mt-5 mb-3">
            <Button
              className="m-auto h-12 font-extrabold text-base rounded-full flex px-2 w-[200px]"
              size="slim"
              onClick={() =>
                !user.isLoggedIn
                  ? handleLogin() : action({ current: "cart", next: nextStep !== "checkout" ? (nextStep) : undefined, goCheckout: (nextStep === "checkout") })
              }
            >
              {t("client_buttons_continue")}
            </Button>
          </div>
        </div>
      </Modal>
      <div className="mt-10 mb-5 w-full relative">
        <p className="text-center lg:text-[32px] md:text-2xl  text-2xl font-extrabold text-textDark">
          <span>{t(
            "client_cart_cart"
          )}</span>
          <span className=" px-1 md:inline-block hidden">{ItemsQty ? `(${convertNumber(router.locale, ItemsQty)})` : ""}</span></p>
      </div>
      {cartProblems.find((p) => p.type === "less-registry-item") ?
        (<p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 mt-10">
          {t("client_cart_registry_less_itemremovehint")}
        </p>
        ) :
        cartProblems.find((p) => p.type === "registry-item-purchased") ?
          (<p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 mt-10">
            {t("client_cart_registry_itempurchasehint")}
          </p>
          ) :
        cartProblems.find((p) => p.type === "balloon-less") ?
          (<p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 mt-10">
            {t("client_cart_balloon_less_itemremovehint")}
          </p>
          ) :
          cartProblems.find((p) => p.type === "non-registry-item") ?
            (<p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 mt-10">
              {t("client_cart_registry_itemremovehint")}
            </p>
            ) : cartProblems.length ? (
              <p className=" text-base font-normal text-[#7A3A00] text-center w-full m-auto bg-[#FFF7DF] rounded-2xl p-4 mt-10">
                {t("client_cart_itemremovehint")}
              </p>
            ) : null}

      {cartItems.length ? <div className="w-full my-4">
        <FreeDelivery />
      </div> : null}

      {cartItems.length ? (
        <>
          {cartItems.map((item) => (
            <CartItem
              step="cart"
              item={item}
              changeQty={(id: string | number, count: number) =>
                changeQty(id, count)
              }
              key={item.id}
              deleteItem={(id: string | number) => deleteItem(id)}
              problem={cartProblems.find((p) => p.id === item.id)}
            />
          ))}
          <Personalizations personalizations={personalizations} personalizationSource={personalizationSource} />
          <div className="w-full mt-5 mb-3">
            <Button
              className="m-auto h-12 font-extrabold text-base rounded-full flex px-2 w-[300px]"
              size="slim"
              onClick={() => {
                action({
                  current: "cart",
                  returnRelatedItems: ((data, nextStep: CartStep | "checkout" | null) => {
                    setExtraItems(data);
                    setNextStep(nextStep || "address");

                    data.length ? setOpenExtraModal(true) : continueAction(nextStep)
                  })
                })
              }}
            >
              {t("client_buttons_continue")}
            </Button>
          </div>
        </>
      ) : (
        <div className="w-full max-w-[480px] mt-20  m-auto">
          <div className=" flex flex-col items-center">
            <p className=" text-[#999] ">
              <BagIcon className=" w-[30px] h-[30px] " stroke="#999" />
            </p>
            <p className=" text-base text-[#999] font-normal text-center mt-5">
              {t("client_cart_nodatamessage")}
            </p>
            <div className="w-full mt-12">
              <Button
                className="m-auto h-12 font-extrabold text-base rounded-full flex px-2 w-[200px]"
                size="slim"
                onClick={() => {
                  router.push("/");
                }}
              >
                {t("client_buttons_startshopping")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <RemoveItemToWishlistModal
        isOpen={showWishlistModal}
        onClose={handleCancelWishlist}
        productName={removedItem?.productName || ""}
        onAddToWishlist={handleAddToWishlist}
        onCancel={handleCancelWishlist}
        isLoading={isAddingToWishlist}
      />
    </div>
  );
};

export default Cart;
